import { DEFAULT_OVERVIEW_TARGETS_SETTINGS, type OverviewTargetsSettings, type TargetPeriod } from './overview';
import { businessDaysElapsedFraction, fyMonths, type GoalMetric } from './stats';

export interface PeriodBounds {
  start: string;
  end: string;
}

function lastDayOfMonth(monthKey: string): string {
  const d = new Date(`${monthKey}-01T00:00:00`);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function periodBounds(periodKey: string): PeriodBounds | null {
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.exec(periodKey);
  if (month) return { start: `${periodKey}-01`, end: lastDayOfMonth(periodKey) };

  const quarter = /^(FY\d{2}) Q([1-4])$/.exec(periodKey);
  if (quarter) {
    const months = fyMonths(quarter[1]);
    const q = Number(quarter[2]);
    const startMonth = months[(q - 1) * 3];
    const endMonth = months[(q - 1) * 3 + 2];
    if (!startMonth || !endMonth) return null;
    return { start: `${startMonth}-01`, end: lastDayOfMonth(endMonth) };
  }
  return null;
}

export interface Pace {
  pct: number;
  elapsedFraction: number;
  onPace: boolean;
}

export function paceOf(target: number, actual: number, periodKey: string, today: string): Pace | null {
  if (!target || target <= 0) return null;
  const bounds = periodBounds(periodKey);
  if (!bounds) return null;
  const elapsedFraction = businessDaysElapsedFraction(bounds.start, bounds.end, today);
  const progress = actual / target;
  return { pct: Math.round(progress * 100), elapsedFraction, onPace: progress >= elapsedFraction };
}

export interface RankableTarget {
  metric: string;
  period: TargetPeriod;
  period_key: string;
  target: number;
  actual: number;
}

export type RankedTarget<T extends RankableTarget> = T & {
  pct: number;
  onPace: boolean;
  deficit: number;
};

export function filterTargetsByPeriod<T extends Pick<RankableTarget, 'metric' | 'period'>>(
  targets: readonly T[],
  settings: Pick<OverviewTargetsSettings, 'target_periods'>,
): T[] {
  return targets.filter(t => settings.target_periods?.[t.metric as GoalMetric]?.includes(t.period) ?? true);
}

export function rankTargetsByPace<T extends RankableTarget>(
  targets: readonly T[],
  today: string,
  settings: Pick<OverviewTargetsSettings, 'targets' | 'targets_max' | 'targets_order' | 'target_periods'> = DEFAULT_OVERVIEW_TARGETS_SETTINGS,
): RankedTarget<T>[] {
  const metricOrder: readonly string[] = settings.targets;
  return filterTargetsByPeriod(targets, settings)
    .filter(t => metricOrder.includes(t.metric))
    .map((t) => {
      const pace = paceOf(t.target, t.actual, t.period_key, today);
      const progress = t.target > 0 ? t.actual / t.target : 0;
      const elapsedFraction = pace?.elapsedFraction ?? 0;
      return {
        ...t,
        pct: pace?.pct ?? Math.round(progress * 100),
        onPace: pace?.onPace ?? true,
        deficit: elapsedFraction - progress,
      };
    })
    .sort((a, b) => {
      if (settings.targets_order === 'pace' && b.deficit !== a.deficit) return b.deficit - a.deficit;
      const metricDiff = metricOrder.indexOf(a.metric) - metricOrder.indexOf(b.metric);
      if (metricDiff) return metricDiff;
      if (a.period !== b.period) return a.period === 'month' ? -1 : 1;
      return 0;
    })
    .slice(0, settings.targets_max);
}
